import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function HowItWorksSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const STEPS = [
    {
      number: "01",
      icon: "ri-walk-fill",
      title: t('how_it_works.steps.0.title'),
      desc: t('how_it_works.steps.0.desc'),
      color: "#13B6EC",
    },
    {
      number: "02",
      icon: "ri-map-pin-2-fill",
      title: t('how_it_works.steps.1.title'),
      desc: t('how_it_works.steps.1.desc'),
      color: "#0E8FBD",
    },
    {
      number: "03",
      icon: "ri-quill-pen-fill",
      title: t('how_it_works.steps.2.title'),
      desc: t('how_it_works.steps.2.desc'),
      color: "#5FCEEF",
    },
    {
      number: "04",
      icon: "ri-medal-fill",
      title: t('how_it_works.steps.3.title'),
      desc: t('how_it_works.steps.3.desc'),
      color: "#13B6EC",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getStyle = (idx: number): React.CSSProperties => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
    transitionDelay: `${idx * 100}ms`
  });

  return (
    <section id="how-it-works" ref={sectionRef} className="py-20 md:py-32 bg-gradient-to-b from-white to-[#f7fbff]">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold text-[#13B6EC] bg-[#13B6EC]/8 tracking-widest uppercase mb-5">
            {t('how_it_works.label')}
          </div>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {t('how_it_works.title')}
          </h2>
          <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            {t('how_it_works.desc')}
          </p>
        </div>

        {/* Steps - Compact text layout */}
        <div className="flex flex-col gap-0">
          {STEPS.map((step, idx) => (
            <div
              key={step.number}
              className="step-item flex gap-5 relative"
              style={getStyle(idx)}
            >
              {/* Left: Icon + connector line */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-2xl z-10"
                  style={{ background: `${step.color}15` }}
                >
                  <i className={`${step.icon} text-xl`} style={{ color: step.color }} />
                </div>
                {/* Connector line */}
                {idx < STEPS.length - 1 && (
                  <div
                    className="w-0.5 flex-1 my-1"
                    style={{ background: `linear-gradient(to bottom, ${step.color}40, ${STEPS[idx + 1].color}40)` }}
                  />
                )}
              </div>

              {/* Right: Content */}
              <div className="pb-8 flex-1">
                <span
                  className="text-[10px] font-black tracking-widest uppercase mb-1 block"
                  style={{ color: step.color }}
                >
                  STEP {step.number}
                </span>
                <h3
                  className="text-lg font-black text-gray-900 mb-1.5"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm text-gray-500 leading-relaxed"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
